/**
 * RECOMMENDATION QUIZ — BUNDLED FALLBACK
 * ============================================================
 * The quiz now lives in Firestore at `app_config/recommendation_quiz`, edited
 * in the admin console (provider app → Admin → Recommendation Quiz).
 *
 * This file is no longer what the app renders. It is the FALLBACK: the six
 * questions exactly as they shipped, used when the document is missing, empty
 * or unreadable. The quiz is the only way a traveller creates an itinerary, so
 * "config did not load" must never mean "no quiz".
 *
 * It is also the seed. `scripts/seed-recommendation-quiz.mjs` in the provider
 * app writes this same list into Firestore, so the switch to Firestore is a
 * visual no-op.
 *
 * SHAPE CHANGED
 * -------------
 * The old export was `quizData.quiz`, with `image` (singular) and options that
 * were bare strings on the destination step and `{value,label}` everywhere
 * else. The canonical shape is now flat and uniform — `images`, `options` as
 * `{value,label}` always, plus `routes` and `enabled`. `hydrateQuestions` in
 * ../utils/recommendationQuiz still accepts the old shape, so a document
 * hand-edited into it keeps working.
 *
 * ROUTES
 * ------
 * `routes: []` means "asked on every route". Everything is, except the stay
 * question, which is Holiday-only: a day trip has nowhere to sleep, so asking
 * a Day Activity traveller to choose between a lodge and a house boat is
 * asking about a night they are not spending. That branch is why the itinerary
 * page skips its "Pick a place to stay" row when `typeofstay` is absent — see
 * App/pages/Itinerary/index.js.
 * ============================================================
 */

const cloudinaryURI = 'https://res.cloudinary.com/pcgwa/image/upload/f_auto,q_auto/q_auto,f_auto/v1687289714/vhakacha/quiz/';

/**
 * The label of the question whose answer picks the route.
 *
 * The date step reads this to choose between a range and a single day, and
 * the itinerary page reads it off the saved itinerary to decide how many days
 * to lay out.
 */
export const ROUTE_LABEL = 'vacationtype';

const choices = (...values) => values.map((value) => ({ value, label: value }));

export const DEFAULT_QUESTIONS = [
  {
    id: 'vacationtype',
    question: 'What would you like to do?',
    type: 'radiogroup',
    label: ROUTE_LABEL,
    routes: [],
    images: [
      `${cloudinaryURI}hiking`,
      'https://res.cloudinary.com/dtdbddo9o/image/upload/f_auto,q_auto/v1693079917/vhakacha/Victoria_Falls/Vic_Falls/safari-lodges/azambezi-lodge-poolside_ixajea.jpg',
      `${cloudinaryURI}Family-cathing-a-fish`,
    ],
    options: choices('Day Activity', 'Holiday'),
    enabled: true,
  },
  {
    id: 'location',
    question: 'Where would you like to go?',
    type: 'locationselect',
    label: 'location',
    routes: [],
    images: [`${cloudinaryURI}woman-relaxing`, `${cloudinaryURI}singita_pamushana_lodge`, `${cloudinaryURI}Zimbabwe-44`],
    // The canonical destinations — the same list as app_config/main and the
    // traveller app's src/data/locations.js. The app REPLACES these at runtime
    // with whatever the config currently says, so this is only the fallback for
    // a failed config read. Kept accurate regardless, so the console never shows
    // a destination list travellers are not actually being offered.
    //
    // The previous list had 18 entries of which 8 were real: ten were provinces
    // and cities no listing can carry, and thirteen genuine destinations —
    // Hwange, Mana Pools, Great Zimbabwe — could not be chosen at all.
    options: choices(
      'Victoria Falls',
      'Hwange National Park',
      'Binga',
      'Kariba',
      'Bumi Hills',
      'Matusadona National Park',
      'Mana Pools',
      'Zvimba',
      'Harare',
      'Lake Chivero',
      'Bulawayo',
      'Matobo Hills',
      'Masvingo',
      'Great Zimbabwe',
      'Gonarezhou National Park',
      'Chiredzi',
      'Mutare',
      'Nyanga',
      'Vumba',
      'Mutarazi',
      'Chimanimani'
    ),
    enabled: true,
  },
  {
    id: 'dates',
    question: 'When do you want to go?',
    type: 'daterange',
    label: 'dates',
    routes: [],
    images: [`${cloudinaryURI}lifes-a-picnic`, `${cloudinaryURI}DSC_0404-1024x682`],
    options: [],
    enabled: true,
  },
  {
    id: 'typeoftravelers',
    question: "Who's coming with you?",
    type: 'radiogroup',
    label: 'typeoftravelers',
    routes: [],
    images: [`${cloudinaryURI}solo-travel`, `${cloudinaryURI}group-travel`],
    options: choices('Traveling Solo', 'Partner', 'Family', 'Friends'),
    enabled: true,
  },
  {
    id: 'typeofstay',
    question: 'What type of stay would you like?',
    type: 'radiogroup',
    label: 'typeofstay',
    routes: ['Holiday'],
    images: [`${cloudinaryURI}407227866`, `${cloudinaryURI}chinzombo`],
    // VALUES ARE LISTING TYPES, LABELS ARE WHAT THE TRAVELLER READS.
    // The answer is compared with `===` against `listing.type`, so an option
    // whose value is not a real category can never return a stay. Four of the
    // five originals could not: 'Safari' is 'Safari Lodges & Camps', 'House
    // Boat' is 'Cruises & Boat Tours', and 'Camping' and 'Back Packing' match
    // no category at all — so a traveller picking Safari got nothing, next to a
    // published safari lodge.
    options: [
      { value: 'Hotels & Lodges', label: 'Hotels & Lodges' },
      { value: 'Safari Lodges & Camps', label: 'Safari' },
      { value: 'Cruises & Boat Tours', label: 'House Boat' },
      { value: 'Fishing Camps', label: 'Fishing Camps' },
      { value: 'Conservation Camps', label: 'Conservation Camps' },
    ],
    enabled: true,
  },
  {
    id: 'interests',
    question: 'What interests you?',
    type: 'multitagselect',
    label: 'interests',
    routes: [],
    images: [`${cloudinaryURI}chef`, `${cloudinaryURI}bungee-jumping`, `${cloudinaryURI}spa-1`],
    options: [
      ...choices('Must-see', 'Culture', 'Outdoors', 'Golf'),
      // Shown capitalised like every other option, but the VALUE stays
      // lowercase: that is the string existing itineraries already carry.
      { value: 'wildlife', label: 'Wildlife' },
      ...choices('Spa', 'Great Food', 'Wine & Beer', 'Adventure', 'Relaxing', 'Hidden Gems'),
      // Shipped with a value and label that disagree. Kept verbatim — the
      // value is what already sits on existing itineraries.
      { value: 'Luxury', label: 'Private Island' },
    ],
    enabled: true,
  },
];
